// AppLayout.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import "../../../center admin css/centerAdmin.css";

import CenterAppointmentsQueue from "./CenterAppointmentsQueue";

// Queue components
import Nurse1PatientQueue from "../Reuse/Nurse1PatientQueue";
import Nurse2PatientQueue from "../Reuse/Nurse2PatientQueue";
import Nurse3PatientQueue from "../Reuse/Nurse3PatientQueue";
import AudiologyPatientQueue from "../Reuse/AudiologyPatientQueue";
import OptometryPatientQueue from "../Reuse/OptometryPatientQueue";

import Logo from "../../../assets/Picture1.png";
import TopBar from "../topbar/TopBar";
import NurseTopBar from "../topbar/NurseTopBar";
import "../../../center admin css/Applayout.css";
import { LogoutButton } from "../../Super Admin/NavBar/NavBar";

export type OutletCtx = {
  activePatient: string;
  setActivePatient: React.Dispatch<React.SetStateAction<string>>;
};

/** Routes where the left queue should be hidden. */
const HIDE_QUEUE_ROUTES = [
  "/check-view",
  "/lab-pickup",
  "/welcome_screen",
  // NOTE: do NOT hide queue on /vision-screening or /audiology/screening
  "/audiology/dashboard",
  "/optometrist/dashboard",
  // hyphen nurse dashboard pages only
  "/nurse-1/dashboard",
  "/nurse-2/dashboard",
  "/nurse-3/dashboard",
  "/",
  "/growth-chart",
  // "/lab-reports-entry",
  "/bnu-sample",
  "/new-dashboard",
  "/nurse-page",
];

export const AppLayout: React.FC = () => {
  const location = useLocation();

  const { pathname } = useLocation();

  const HIDE_HEADER_ROUTES = ["/new-dashboard"];

  const hideQueue =
    pathname === "/" ||
    HIDE_QUEUE_ROUTES.some((r) => r !== "/" && pathname.startsWith(r));

  /**
   * Robust centerId resolver:
   * - Prefer currentUser (if your /users/currentUser writes it)
   * - Also tolerate backendResponse / CurrentResponse (used in Check-In)
   * - Finally, fall back to sessionStorage.centerId if already set elsewhere
   */
  const centerIdFromUser = useMemo(() => {
    const pickCenterId = (obj: any): string | "" => {
      if (!obj || typeof obj !== "object") return "";
      const fromAssign =
        obj?.centerAssignments?.[0]?.centerId ||
        obj?.roleAssignments?.[0]?.centerId ||
        "";
      return typeof fromAssign === "string" ? fromAssign : "";
    };

    const readJSON = (store: Storage, key: string) => {
      try {
        const raw = store.getItem(key);
        return raw ? JSON.parse(raw) : null;
      } catch {
        return null;
      }
    };

    // Prefer sessionStorage currentUser
    const sCurrent = readJSON(sessionStorage, "currentUser");
    if (sCurrent) {
      const cid = pickCenterId(sCurrent);
      if (cid) return cid;
    }

    // Accept legacy / cached keys used by Check-In (both stores)
    const keys = ["backendResponse", "CurrentResponse", "CurrrentResponse"];
    for (const k of keys) {
      const s = readJSON(sessionStorage, k);
      if (s) {
        const cid = pickCenterId(s);
        if (cid) return cid;
      }
      const l = readJSON(localStorage, k);
      if (l) {
        const cid = pickCenterId(l);
        if (cid) return cid;
      }
    }

    // Also allow localStorage currentUser
    const lCurrent = readJSON(localStorage, "currentUser");
    if (lCurrent) {
      const cid = pickCenterId(lCurrent);
      if (cid) return cid;
    }

    // last resort: if someone already persisted it
    const stored = sessionStorage.getItem("centerId");
    return stored || "";
  }, []);

  // Keep a normalized copy so other places can simply read sessionStorage.centerId
  useEffect(() => {
    if (
      centerIdFromUser &&
      sessionStorage.getItem("centerId") !== centerIdFromUser
    ) {
      sessionStorage.setItem("centerId", centerIdFromUser);
    }
  }, [centerIdFromUser]);

  // initialize empty; the queue will auto-select first fetched item
  // const [activePatientId, setActivePatientId] = useState<string>("");
  const [activePatient, setActivePatient] = useState<any>(null);

  const TABS = [
    "Home",
    "Check-In",
    "Growth Chart",
    "Lab Pick-Up",
    // "Lab Reports Entry",
    "B&U sample",
    "Nurse",
  ];

  const linkForTab = (tab: string): string => {
    switch (tab) {
      case "Home":
        return "/welcome_screen";
      case "Check-In":
        return "/check-view";
      case "Growth Chart":
        return "/growth-chart";
      case "Lab Pick-Up":
        return "/lab-pickup";
      case "Lab Reports Entry":
        return "lab-reports-entry";
      case "B&U sample":
        return "/bnu-sample";
      case "Nurse":
        return "/nurse-page";
      default:
        return "#";
    }
  };

  // single-column when queue hidden
  const bodyStyle = hideQueue
    ? ({ gridTemplateColumns: "1fr" } as React.CSSProperties)
    : undefined;

  const mainStyle = hideQueue
    ? ({ width: "100%", maxWidth: "100%" } as React.CSSProperties)
    : undefined;

  /**
   * Compact topbar for clinical flows
   */
  const compactTopbar = pathname.startsWith("/nurse-1")
    ? { basePath: "/nurse-1", label: "Nurse 1" }
    : pathname.startsWith("/nurse-2")
      ? { basePath: "/nurse-2", label: "Nurse 2" }
      : pathname.startsWith("/nurse-3")
        ? { basePath: "/nurse-3", label: "Nurse 3" }
        : pathname.startsWith("/audiology")
          ? { basePath: "/audiology", label: "Audiologist Screening" }
          : pathname.startsWith("/optometrist") ||
              pathname.startsWith("/vision-screening")
            ? { basePath: "/optometrist", label: "Vision Screening" }
            : null;

  const onQueueSelect = (patient: any) => {
    setActivePatient(patient);
    console.log("APP LAYOUT RECEIVED", patient);
  };

  useEffect(() => {
    console.log("ACTIVE PATIENT CHANGED", activePatient);
  }, [activePatient]);

  type QueueCfg =
    | { show: true; node: React.ReactNode }
    | { show: false; node?: undefined };

  const queueCfg: QueueCfg = (() => {
    if (hideQueue) return { show: false };

    if (pathname.startsWith("/nurse-1")) {
      return {
        show: true,
        node: (
          <Nurse1PatientQueue
            centerId={centerIdFromUser || " "}
            stationId="NURSE1"
            autoRefreshMs={30000}
            activeId={activePatient?._id}
            onSelect={onQueueSelect}
          />
        ),
      };
    }
    if (pathname.startsWith("/nurse-2")) {
      return {
        show: true,
        node: (
          <Nurse2PatientQueue
            centerId={centerIdFromUser || " "}
            stationId="NURSE2"
            autoRefreshMs={0}
            activeId={activePatient?._id}
            onSelect={onQueueSelect}
          />
        ),
      };
    }
    if (pathname.startsWith("/nurse-3")) {
      return {
        show: true,
        node: (
          <Nurse3PatientQueue
            centerId={centerIdFromUser || " "}
            stationId="NURSE3"
            autoRefreshMs={30000}
            activeId={activePatient?._id}
            onSelect={onQueueSelect}
          />
        ),
      };
    }
    if (pathname.startsWith("/audiology/screening")) {
      return {
        show: true,
        node: (
          <AudiologyPatientQueue
            centerId={centerIdFromUser || " "}
            stationId="AUDIOLOGY"
            autoRefreshMs={30000}
            activeId={activePatient?._id}
            onSelect={onQueueSelect}
          />
        ),
      };
    }
    if (pathname.startsWith("/vision-screening")) {
      return {
        show: true,
        node: (
          <OptometryPatientQueue
            centerId={centerIdFromUser || " "}
            stationId="OPTOMETRY"
            autoRefreshMs={30000}
            activeId={activePatient?._id}
            onSelect={onQueueSelect}
          />
        ),
      };
    }

    // fallback: dynamic appointments queue (Growth Chart / Lab Reports Entry / B&U sample / Forms)
    return {
      show: true,
      node: (
        <CenterAppointmentsQueue
          centerId={centerIdFromUser || ""}
          dateISO={new Date().toISOString().slice(0, 10)}
          autoRefreshMs={30000}
          activeId={activePatient?._id}
          onSelect={onQueueSelect}
        />
      ),
    };
  })();

  return (
    <div className="shell">
      {!HIDE_HEADER_ROUTES.includes(pathname) && (
        <header className="app-header">
          {compactTopbar ? (
            <NurseTopBar
              basePath={compactTopbar.basePath}
              label={compactTopbar.label}
            />
          ) : (
            <div className="app-header__inner">
              <img className="app-header__logo" src={Logo} alt="Qavach" />
              <div style={{ flex: 1 }}>
                <TopBar
                  tabs={TABS}
                  linkForTab={linkForTab}
                  logoSrc={undefined}
                />
              </div>
            </div>
          )}
        </header>
      )}

      <div
        className={`shell-body${queueCfg.show ? " shell-body--with-queue" : ""}`}
        style={bodyStyle}
      >
        {/* LEFT SIDEBAR */}
        {queueCfg.show && (
          <aside className="shell-queue">{queueCfg.node}</aside>
        )}

        {/* MAIN CONTENT */}
        <div className="shell-main" style={mainStyle}>
          <Outlet
            context={{ activePatient, setActivePatient } satisfies OutletCtx}
          />
        </div>
      </div>
    </div>
  );
};

export default AppLayout;
